/**
=========================================================
* Soft UI Dashboard PRO React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-material-ui
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// Core
import Router from "Router";
import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

// Components
import CssBaseline from "@mui/material/CssBaseline";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import ConfirmDialog from "components/Feedback/ConfirmDialog";
import { Toasts } from "components/AtomsV2";
import { Layout as RecLayout } from "components/ShellV2";

// Theme
import createAppTheme from "assets/theme";
import RecThemeProvider from "theme/RecThemeProvider";

// Redux
import { useSelector } from "react-redux";
import { selectThemeMode } from "state/application/applicationSlice";
import { selectRole } from "state/auth/authSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";

// Utils
import { isStandalonePath } from "utils/standalonePages";

// Casl
import { buildAbilityFor } from "config/rulesForRoles";
import { AbilityContext } from "context/Ability.context";

// Others
import ComunityTree from "components/Drawers/CommunityTree";

export default function App() {
  const { pathname } = useLocation();

  const role = useSelector(selectRole);
  const community = useSelector(selectCurrentCommunity);
  const isCommunity = role && community;
  // Handle permissions
  const ability = buildAbilityFor(role);

  // Check if current page is an auth page (signin, signout, signup, etc.)
  const isAuthPage =
    pathname === "/" ||
    pathname.startsWith("/login") ||
    pathname.startsWith("/signin") ||
    pathname.startsWith("/signout") ||
    pathname.startsWith("/signup") ||
    pathname.startsWith("/forgot-password") ||
    pathname.startsWith("/reset-password") ||
    pathname.startsWith("/password_reset") ||
    pathname.startsWith("/change-password");

  // Some full-page tools carry their own chrome, so render them outside the
  // shell.
  const isStandalonePage = isStandalonePath(pathname);

  // Theme mode
  const themeMode = useSelector(selectThemeMode);
  const theme = useMemo(() => createAppTheme(themeMode), [themeMode]);

  useEffect(() => {
    // Setting page scroll to 0 when changing the route
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  return (
    <RecThemeProvider>
      <StyledEngineProvider injectFirst>
        <AbilityContext.Provider value={ability}>
          <ThemeProvider theme={theme}>
            <CssBaseline />

            {isAuthPage || isStandalonePage ? (
              <>
                <Router />
                <ConfirmDialog />
              </>
            ) : (
              <RecLayout>
                {isCommunity && <ComunityTree community={community} />}
                <Router />
                <ConfirmDialog />
              </RecLayout>
            )}

            <Toasts />
          </ThemeProvider>
        </AbilityContext.Provider>
      </StyledEngineProvider>
    </RecThemeProvider>
  );
}
